import { FC, useEffect, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import usePlayerChangeController from 'application/hooks/usePlayerChangeController';
import { useAccountCompanyId } from 'application/hooks';
import { useUserBillingInfo } from '@entities/billing';

import { DeviceTypes, PaymentKind } from '@shared/api/graphql/generated';

import Input from '@zvuk-b2b/react-uikit/ui/Input';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { TEXTS } from './texts';
import { FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING, FLOWS_FOR_SHOW_DEVICE_BIND_SUCCESS } from '../../consts';
import { useOnboardingContext } from '../../useOnboardingContext';
import { useOnboardingActionContext } from '../../useOnboardingActionContext';

import styles from './BindVideoModal.module.scss';
import { SelectPlayerContentViewMode } from '../../types';

const DEVICE_IMAGE_PATH = '/assets/img/app/video/tv.png';

const BindVideoModal: FC<ModalProps[ModalTypes.VIDEO_PLAYER_BIND]> = ({ openModal, replaceModal, close }) => {
  const { flowType, currentPoint, contentViewMode } = useOnboardingContext();
  const { setInFlow, clearCurrentPoint } = useOnboardingActionContext();
  const companyId = useAccountCompanyId();

  const { watchBillingInfo } = useUserBillingInfo();
  const { hasInactiveLicenses, selectedPaymentKind } = watchBillingInfo(companyId);

  const isShowPaymentModal = () => {
    const hasDevice = Boolean(currentPoint?.device?.type);
    const hasStream = Boolean(currentPoint?.stream?.id);

    if (selectedPaymentKind === PaymentKind.Sbbol) {
      return hasInactiveLicenses && hasDevice && hasStream;
    }

    return currentPoint?.isEnabled === false && hasDevice && hasStream;
  };

  const onSuccessCallback = () => {
    setInFlow(false);

    if ((flowType && FLOWS_FOR_SHOW_DEVICE_BIND_SUCCESS.has(flowType)) || currentPoint?.isVideoDemo) {
      replaceModal(ModalTypes.DEVICE_BIND_SUCCESS, {});

      return;
    }

    if (isShowPaymentModal()) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else if (contentViewMode === SelectPlayerContentViewMode.VIDEO_STREAM) {
      replaceModal(ModalTypes.DEVICE_BIND_SUCCESS, {});
    } else {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, {});
    }
  };

  const { bindingCode, setBindingCode, bindingCodeError, handleUpdateClick, setToPlayerType } =
    usePlayerChangeController({
      pointId: currentPoint?.id || '',
      fromPlayerType: DeviceTypes.Ios, // TODO: don't matter which mobile type will be passed
      onSuccessCallback,
      isVideoMode: true,
    });

  useEffect(() => {
    setToPlayerType('APP');
  }, []);

  const handleCancel = () => {
    if (flowType && FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING.has(flowType)) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      title={TEXTS.MODAL_TITLE}
      className={styles.modalContainer}
      contentClassName={styles.modalContent}
      containerActionButtonsClassName={styles.actionButtons}
      actionButtons={[
        <Button
          key="button_bind_video_modal_success"
          elementId="button_bind_video_modal_success"
          type={ButtonType.PRIMARY}
          isDisabled={bindingCode.length < 4}
          onClick={handleUpdateClick}>
          {TEXTS.SUCCESS_BUTTON}
        </Button>,
        <Button
          key="button_bind_video_modal_cancel"
          elementId="button_bind_video_modal_cancel"
          type={ButtonType.SECONDARY}
          onClick={() => {
            replaceModal(ModalTypes.DOWNLOAD_TV_APP, {});
          }}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      <div className={styles.description}>{TEXTS.TITLE_DESCRIPTION}</div>

      <div className={styles.downloadContent}>
        <Input
          elementId="button_bind_video_modal_code_input"
          onChange={setBindingCode}
          value={bindingCode}
          placeholder={TEXTS.CODE_BIND_PLACEHOLDER}
          maxLength={4}
          label={TEXTS.CODE_BIND_TITLE}
          error={bindingCodeError}
        />

        <img
          src={DEVICE_IMAGE_PATH}
          alt=""
          loading="lazy"
          className={styles.bindImage}
        />
      </div>
    </ModalScreen>
  );
};

export default memo(BindVideoModal);
